import { TranslationNamespaceEnum } from "src/lib/i18next/types.ts";
import { mapObjectToArray } from "@exonn/utils";
import { AlertList } from "src/components/AlertV2/AlertList.tsx";
import type { AccountingPostingValidationErrorData } from "src/components/Form/hooks/useResourceForm/useResourceForm.tsx";
import { useErpTranslation } from "src/system/useErpTranslation.ts";
import { ResourceEnum } from "src/constants/constants.ts";
import { type FormAction } from "src/components/Form/Form.types.ts";

export const AccountingPostingValidationErrorImpl = ({
  data,
  type,
}: {
  data: AccountingPostingValidationErrorData;
  type: FormAction;
}) => {
  const { errors } = data;

  const { t } = useErpTranslation({
    type: "form",
    resource: ResourceEnum.AccountingPosting,
    formType: type,
  });

  const postingErrors = errors
    .filter((error) => {
      return (
        !error.context.position_number ||
        (!!error.context.position_number && !!error.context.posting_number)
      );
    })
    .map((error) => {
      return {
        title: t(`postingErrors.${error.code}`, {
          defaultValue: error.message,
          ...error.context,
        }),
      };
    }, {});

  const position_errors = errors
    .filter((error) => {
      return !!error.context.position_number && !error.context.posting_number;
    })
    .reduce(
      (acc, error) => {
        const positionNumber = error.context.position_number!;
        const positionErrors = acc[positionNumber];
        return {
          ...acc,
          [positionNumber]: {
            ...positionErrors,
            [error.code]: error.message,
          },
        };
      },
      {} as Record<number, Record<number, string>>,
    );

  const positionErrors = mapObjectToArray(
    position_errors,
    (value, positionNumber) => {
      return {
        title: t(`errorsForPosition`, {
          number: positionNumber,
        }),
        items: mapObjectToArray(value, (errorText, code) => {
          return t(`postingErrors.${code}`, {
            defaultValue: errorText,
          });
        }),
      };
    },
  ).flat();

  const items = [...postingErrors, ...positionErrors];
  return (
    <AlertList
      title={t("notifications.formSaveFailed", {
        ns: TranslationNamespaceEnum.FormComponent,
      })}
      items={items}
    />
  );
};
